/* eslint-disable no-underscore-dangle -- Okta API uses _embedded */
import type { OktaAuthnResponse } from '@hooks/oktaAuthnTypes'

export { isPlausibleMfaEnrollmentEmail, resolveMfaEmailForOktaEnrollProfile, resolveMfaEmailFromFormAndContext } from './mfaFormContextEmail'

const coalesceTrimmed = (...candidates: Array<unknown>): string => {
  const first = candidates.find((c) => c != null && String(c).trim().length > 0)
  return first == null ? '' : String(first).trim()
}

const trimmedFormField = (v: unknown): string => (v == null ? '' : String(v).trim())

const findAuthnEmailFactor = (authn: OktaAuthnResponse) =>
  authn._embedded?.factors?.find((f: { factorType?: string }) => f.factorType === 'email') ||
  authn.data?._embedded?.factors?.find((f: { factorType?: string }) => f.factorType === 'email')

type AuthnEmailFactor = NonNullable<ReturnType<typeof findAuthnEmailFactor>>

const firstEmailFromAuthnEmailFactor = (emailFactor: AuthnEmailFactor | undefined): string => {
  if (!emailFactor) return ''
  const list = emailFactor._embedded?.emails
  if (!Array.isArray(list) || list.length === 0) return ''
  const first = list[0] as { email?: string; profile?: { email?: string } }
  return coalesceTrimmed(first?.email, first?.profile?.email)
}

const emailFromPrimaryEnrolledFactor = (factor?: { factorType?: string; profile?: { email?: string } }): string => {
  if (factor?.factorType === 'email' && factor.profile?.email) {
    return trimmedFormField(factor.profile.email)
  }
  return ''
}

const resolveMfaEmailFromOktaAuthn = (
  authnResponse: OktaAuthnResponse,
  username: string,
  primaryEnrolledFactor?: { factorType?: string; profile?: { email?: string } },
): string => {
  const emailFactor = findAuthnEmailFactor(authnResponse)
  return coalesceTrimmed(
    authnResponse._embedded?.user?.profile?.email,
    authnResponse.data?._embedded?.user?.profile?.email,
    firstEmailFromAuthnEmailFactor(emailFactor),
    authnResponse.user?.profile?.email,
    emailFromPrimaryEnrolledFactor(primaryEnrolledFactor),
    username,
  )
}

/**
 * Resolves a displayable email for MFA (code sent to… / registration context).
 * Same sources as AuthService MFA_ENROLL: embedded user, email factor's `_embedded.emails`,
 * user profile, enrolled primary email factor profile, then username.
 */
export const resolveAuthnMfaDisplayEmail = (
  authnResponse: OktaAuthnResponse,
  username: string,
  primaryEnrolledFactor?: { factorType?: string; profile?: { email?: string } },
): string => resolveMfaEmailFromOktaAuthn(authnResponse, username, primaryEnrolledFactor)
